'use client'

import { useEffect, useRef } from 'react'

// ChunkLoadErrorを検出する関数
function isChunkLoadError(error: Error): boolean {
  return (
    error.name === 'ChunkLoadError' ||
    error.message.includes('Loading chunk') ||
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Failed to fetch RSC payload')
  )
}

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const hasReloadedRef = useRef(false)

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.error('[Global error]', error)
    }

    // ChunkLoadErrorの場合は自動的にページをリロード
    if (isChunkLoadError(error) && !hasReloadedRef.current) {
      hasReloadedRef.current = true
      console.log('[Global error] ChunkLoadError detected, reloading page...')
      // キャッシュをクリアしてリロード
      window.location.reload()
      return
    }
  }, [error])

  // ChunkLoadErrorの場合はリロード中なので何も表示しない
  if (isChunkLoadError(error) && hasReloadedRef.current) {
    return (
      <html lang="en">
        <body style={{ margin: 0, fontFamily: 'system-ui, sans-serif', background: '#fafafa', color: '#171717' }}>
          <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: 48, height: 48, border: '3px solid #e5e5e5', borderTopColor: '#171717', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
              <p style={{ color: '#737373' }}>Reloading page...</p>
            </div>
          </div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </body>
      </html>
    )
  }

  return (
    <html lang="en">
      <body style={{ margin: 0, fontFamily: 'system-ui, sans-serif', background: '#fafafa', color: '#171717' }}>
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            boxSizing: 'border-box',
          }}
        >
          <div style={{ maxWidth: 420, textAlign: 'center' }}>
            <h1 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: 8 }}>
              Something went wrong
            </h1>
            <p style={{ color: '#737373', marginBottom: 24, lineHeight: 1.5 }}>
              An unexpected error occurred. Please try again or return to the homepage.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => reset()}
                style={{
                  padding: '10px 16px',
                  background: '#171717',
                  color: '#fafafa',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                Try again
              </button>
              <a
                href="/"
                style={{
                  padding: '10px 16px',
                  background: 'transparent',
                  color: '#171717',
                  border: '1px solid #e5e5e5',
                  borderRadius: 8,
                  textDecoration: 'none',
                  fontWeight: 500,
                }}
              >
                Go home
              </a>
            </div>
          </div>
        </div>
      </body>
    </html>
  )
}
